import {
  CONTRACT_TYPE_SERIES_OPTIONS, SYSTEM_AUTO_CONTRACT_TYPE, NON_CONTRACT_DOCUMENT_SERIES,
  resolveContractTypeSeries, isLegalInstrumentLabel, formatContractTypePreviewLabel,
  LEGACY_CONTRACT_TYPE_TO_SERIES,
} from './legalContractTypeSeries.js';

export const CONTRACT_CLASSIFICATION_VERSION = 4;
/** 目录仅指向现有审核引擎，不因模型描述自动创建专项规则。 */
export const CONTRACT_REVIEW_RULE_CATALOG = [
  {id:'sale', series:CONTRACT_TYPE_SERIES_OPTIONS[0], engineType:'买卖合同', boundary:'以货物、设备或房屋所有权转移和支付价款为核心；不含股权交易、融资租赁。'},
  {id:'governance', series:CONTRACT_TYPE_SERIES_OPTIONS[1], engineType:'股权交易与公司治理协议', boundary:'股权/股份/产权交易、增资、股东治理及合伙；不因名称中有合作就自动适用。'},
  {id:'construction', series:CONTRACT_TYPE_SERIES_OPTIONS[2], engineType:'建设工程合同', boundary:'以勘察、设计、施工、工程承包、监理、项目管理给付为核心；工程背景的检测或咨询成果不当然属于本类。'},
  {id:'lease', series:CONTRACT_TYPE_SERIES_OPTIONS[3], engineType:'租赁合同', boundary:'以让渡资产使用收益权及租金/融资租赁安排为核心；合作经营涉及对外出租不等于双方构成租赁。'},
  {id:'service', series:CONTRACT_TYPE_SERIES_OPTIONS[4], engineType:'承揽、技术与服务合同', boundary:'以完成工作成果、承揽、技术开发/转让/许可/咨询/服务等为核心；背景提及原开发合同不决定当前协议性质。'},
  {id:'mandate', series:CONTRACT_TYPE_SERIES_OPTIONS[5], engineType:'委托及经营管理合同', boundary:'以受托处理事务、委托经营管理、行纪、中介、托管等为核心；需区分共同经营和单纯受托管理。'},
  {id:'finance', series:CONTRACT_TYPE_SERIES_OPTIONS[6], engineType:'融资与担保合同', boundary:'借贷、保证、抵押、质押、保理、独立保函等融资担保安排；不含融资租赁。'},
  {id:'logistics', series:CONTRACT_TYPE_SERIES_OPTIONS[7], engineType:'运输、保管与仓储合同', boundary:'以运输、保管、仓储或综合物流给付为核心；买卖合同附带运输不自动改变主分类。'},
  {id:'special-law', series:CONTRACT_TYPE_SERIES_OPTIONS[8], engineType:'特别法合同系列', boundary:'劳动人事、劳务派遣、竞业限制、保密等现有特别法专项。'},
  {id:'other-mixed', series:CONTRACT_TYPE_SERIES_OPTIONS[9], engineType:'其他合同', boundary:'现有意向/备忘录、无名及多种主要给付难以归入单一有名系列的混合合同；不是不确定时的自动兜底。'},
  {id:'other-mixed.settlement', series:CONTRACT_TYPE_SERIES_OPTIONS[9], engineType:'和解协议', boundary:'以解决既有争议、让步清偿、调解或执行和解为主要目的；原交易作为背景，不覆盖本协议的争议解决性质。'},
  {id:'non-contract', series:NON_CONTRACT_DOCUMENT_SERIES, engineType:NON_CONTRACT_DOCUMENT_SERIES, boundary:'情况报告、工作汇报、会议纪要、函件、制度草案、法律意见书、尽调报告等不以缔约给付为核心的文书；不套用有名合同专项规则，仅对照相关法律法规与企业内部合规管理制度审核。'},
] as const;

export type ContractAnalogy = {
  ruleId: string;
  reason: string;
  appliesTo: string;
  evidence: string[];
};

export type ContractClassification = {
  version: number;
  source: 'model' | 'manual' | 'unavailable';
  status: 'accepted' | 'needs-confirmation';
  ruleId: string;
  series: string;
  specificType: string;
  reviewContractType: string;
  confidence: number;
  reason: string;
  evidence: string[];
  supplementalChecks: string[];
  analogousRules: ContractAnalogy[];
  warnings: string[];
  fullTextCovered: boolean;
};

const str=(v:unknown,max=600)=>typeof v==='string'?v.trim().slice(0,max):'';
/** 模型偶尔把单条证据写成字符串，统一为数组后仍逐条核对正文。 */
const quoteList=(v:unknown):unknown[]=>Array.isArray(v)?v:typeof v==='string'&&v.trim()?[v]:[];
const norm=(s:string)=>s.replace(/\s+/gu,'');
const engineSeries=(s:string)=>s==='特别法合同'?CONTRACT_TYPE_SERIES_OPTIONS[8]
  :s===NON_CONTRACT_DOCUMENT_SERIES||s==='非合同'?NON_CONTRACT_DOCUMENT_SERIES
  :CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.engineType===s)?.series||resolveContractTypeSeries(s);

/** 是否为非合同文件审核路径（跳过合同专项与近似规则）。 */
export function isNonContractClassificationRule(ruleId:string):boolean{
  return ruleId==='non-contract';
}

export function pendingContractClassification(warning='尚未完成基于全文的合同分类，请重新识别或在项目设置中人工确认类别。'):ContractClassification{
  return {version:CONTRACT_CLASSIFICATION_VERSION,source:'unavailable',status:'needs-confirmation',ruleId:'',series:'',specificType:'',reviewContractType:'',confidence:0,reason:'',evidence:[],supplementalChecks:[],analogousRules:[],warnings:[warning],fullTextCovered:false};
}

/**
 * 是否需要为合同确定近似有名合同规则。
 * 仅无名/混合合同需要；意向书、备忘录、预约合同按原通用规则审核。
 * 用户手动确认的混合合同可以没有近似规则，此时只适用通用审核规则。
 * 非合同文件不走近似规则。
 */
export function needsContractAnalogy(ruleId:string,specificType:string):boolean{
  if(isNonContractClassificationRule(ruleId))return false;
  return ruleId==='other-mixed'&&!/^(?:意向书|合作备忘录|备忘录|预约合同)$/u.test(specificType);
}

/** 只允许引用系统已有的有名合同主规则，不能引用其他/混合本身或其子规则，也不能引用非合同。 */
export function getContractAnalogyRule(ruleId:string){
  return CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.id===ruleId&&r.id!=='other-mixed'&&r.id!=='non-contract'&&!r.id.includes('.'));
}

export function validateModelContractClassification(raw:unknown,text:string,fullTextCovered=true):ContractClassification{
  const v=raw&&typeof raw==='object'?raw as Record<string,unknown>:{};
  const rule=CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.id===v.ruleId);
  if(!rule)return pendingContractClassification('模型未选定系统已有的唯一主审核规则，请重新识别或人工确认。');
  const reason=str(v.reason,420),specificType=str(v.specificType,80);
  const confidence=typeof v.confidence==='number'&&Number.isFinite(v.confidence)?Math.max(0,Math.min(100,Math.round(v.confidence))):0;
  const title=norm(text.trim().split(/\r?\n/u)[0]??'');
  const evidence=[...new Set(quoteList(v.evidence).map(x=>str(x,200)).filter(x=>norm(x).length>=6&&norm(x)!==title&&norm(text).includes(norm(x))))].slice(0,5);
  const warnings:string[]=[];
  if(!reason||!evidence.length)warnings.push('分类缺少可核验的正文事实依据，请人工确认。');
  if(confidence<75)warnings.push(isNonContractClassificationRule(rule.id)
    ?'模型对「非合同文件」判断的置信度不足，请人工确认。'
    :'模型对合同主类别的判断置信度不足，请人工确认。');
  if(!fullTextCovered)warnings.push('本次分类未覆盖完整正文，不能直接采用，请人工确认。');
  const analogousRules:ContractAnalogy[]=[];
  const requested=Array.isArray(v.analogousRules)?v.analogousRules:[];
  if(isNonContractClassificationRule(rule.id)&&requested.length)warnings.push('非合同文件不得启用合同近似规则。');
  if(requested.length>2)warnings.push('混合合同最多选择两种近似规则，请重新识别。');
  if(!needsContractAnalogy(rule.id,specificType)&&requested.length&&!isNonContractClassificationRule(rule.id))warnings.push('只有无名或混合合同可以启用近似合同规则，不能改变其他合同的主类别。');
  const seen=new Set<string>();
  if(!isNonContractClassificationRule(rule.id)){
    for(const entry of requested.slice(0,2)){
      const a=entry&&typeof entry==='object'?entry as Record<string,unknown>:{};
      const id=str(a.ruleId,60),target=getContractAnalogyRule(id);
      const quotes=[...new Set(quoteList(a.evidence).map(q=>str(q,200)).filter(q=>norm(q).length>=6&&norm(q)!==title&&norm(text).includes(norm(q))))].slice(0,4);
      const appliesTo=str(a.appliesTo,220),why=str(a.reason,240);
      if(!target||seen.has(id)||!appliesTo||!why||!quotes.length){warnings.push('近似合同规则必须有效、互不重复，并说明适用义务及真实正文依据。');continue;}
      seen.add(id);analogousRules.push({ruleId:id,reason:why,appliesTo,evidence:quotes});
    }
  }
  if(needsContractAnalogy(rule.id,specificType)&&!analogousRules.length)warnings.push('无名或混合合同尚未确定有依据的近似合同规则，请重新识别或人工确认。');
  // 说明仅提供事实，不允许夹带第二套分类或编造专项规则。
  if(/专项.*规则|建议按|应归入/u.test(reason))warnings.push('模型在事实依据中夹带了审核规则推荐，请重新识别或人工确认。');
  if(CONTRACT_TYPE_SERIES_OPTIONS.some(s=>s!==rule.series&&reason.includes(s)))warnings.push('模型理由与所选主类别冲突，请重新识别或人工确认。');
  const supplementalChecks=Array.isArray(v.supplementalChecks)?v.supplementalChecks.map(x=>str(x,160)).filter(x=>x&&!/专项.*规则|建议按|应归入|系列/u.test(x)).slice(0,5):[];
  // 括号里的背景交易（如「权利义务转让协议（物业服务协议）」）不参与冲突判断。
  const primarySpecific=specificType.split(/[（(]/u)[0]?.trim()||specificType;
  const mapped=engineSeries(primarySpecific);
  // 其他或混合合同的具体名称可以点出底层交易（物业服务、租赁等），主类别仍以模型选定的规则为准。
  if(rule.id!=='other-mixed'&&!isNonContractClassificationRule(rule.id)&&primarySpecific&&!isLegalInstrumentLabel(primarySpecific)&&mapped!=='其他或混合合同'&&mapped!==rule.series)warnings.push('具体合同类型与主类别冲突，请重新识别或人工确认。');
  const reviewContractType=isNonContractClassificationRule(rule.id)?rule.engineType
    :rule.id==='other-mixed.settlement'?rule.engineType
    :specificType&&mapped===rule.series&&!isLegalInstrumentLabel(specificType)&&!(CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(specificType)
      ?specificType:rule.engineType;
  return {version:CONTRACT_CLASSIFICATION_VERSION,source:'model',status:warnings.length?'needs-confirmation':'accepted',ruleId:rule.id,series:rule.series,specificType,reviewContractType,confidence,reason,evidence,supplementalChecks,analogousRules,warnings,fullTextCovered};
}

/** 客户端和读取存量记录时同样校验结构，绝不从说明/文件名重新推断系列。 */
export function isCurrentContractClassification(value:unknown):value is ContractClassification{
  if(!value||typeof value!=='object')return false;
  const c=value as ContractClassification;
  if(c.version!==CONTRACT_CLASSIFICATION_VERSION||!['model','manual','unavailable'].includes(c.source)||!['accepted','needs-confirmation'].includes(c.status))return false;
  if(![c.warnings,c.evidence,c.supplementalChecks].every(a=>Array.isArray(a)&&a.every(x=>typeof x==='string')))return false;
  if(![c.ruleId,c.series,c.specificType,c.reviewContractType,c.reason].every(x=>typeof x==='string')||!Number.isFinite(c.confidence))return false;
  if(!Array.isArray(c.analogousRules)||c.analogousRules.length>2)return false;
  const ids=new Set<string>();
  for(const a of c.analogousRules){
    if(!a||!getContractAnalogyRule(a.ruleId)||ids.has(a.ruleId)||typeof a.reason!=='string'||!a.reason.trim()||typeof a.appliesTo!=='string'||!a.appliesTo.trim()||!Array.isArray(a.evidence)||!a.evidence.length||!a.evidence.every(q=>typeof q==='string'&&norm(q).length>=6))return false;
    ids.add(a.ruleId);
  }
  if(c.status!=='accepted')return true;
  if(isNonContractClassificationRule(c.ruleId)){
    const rule=CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.id===c.ruleId);
    return Boolean(rule&&c.series===rule.series&&c.reviewContractType===rule.engineType&&!c.analogousRules.length
      &&(c.source==='manual'||(c.fullTextCovered&&c.confidence>=75&&c.reason&&c.evidence.length)));
  }
  if(needsContractAnalogy(c.ruleId,c.specificType)?(c.source!=='manual'&&!c.analogousRules.length):c.analogousRules.length>0)return false;
  const rule=CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.id===c.ruleId);
  return Boolean(rule&&c.series===rule.series&&c.reviewContractType&&engineSeries(c.reviewContractType)===c.series
    &&(!rule.id.includes('.')||c.reviewContractType===rule.engineType)
    &&(c.source==='manual'||(c.fullTextCovered&&c.confidence>=75&&c.reason&&c.evidence.length)));
}

export function contractClassificationPreview(c:ContractClassification){
  const accepted=isCurrentContractClassification(c)&&c.status==='accepted';
  const series=accepted?c.series:'待确认';
  const specific=accepted?c.specificType:'';
  // 只用受控目录生成唯一主规则句，不拼接模型的旧说明。
  const rule=CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.id===c.ruleId);
  const analogy=!accepted?'':c.analogousRules.length?`近似规则：${c.analogousRules.map(a=>`${getContractAnalogyRule(a.ruleId)?.series}（限于${a.appliesTo}）`).join('；')}。`
    :needsContractAnalogy(c.ruleId,c.specificType)?'未启用近似规则，仅按通用审核规则审核。':'';
  const mainRule=isNonContractClassificationRule(c.ruleId)
    ?'非合同文件 · 对照相关法律法规与企业内部合规管理制度（不套用合同专项规则）'
    :rule?.id==='other-mixed.settlement'?'和解/调解协议专项规则':c.series;
  const analysis=accepted?`${c.reason}${/[。；]$/u.test(c.reason)?'':'。'}主审核规则：${mainRule}。${analogy}`
    :c.warnings.join(' ')||'合同主类别需要人工确认。';
  return {series,specific,label:formatContractTypePreviewLabel(series,specific),analysis,reviewContractType:accepted?c.reviewContractType:''};
}

export function manualContractClassification(selected:string,prior?:ContractClassification):ContractClassification{
  if(!(CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(selected)&&!LEGACY_CONTRACT_TYPE_TO_SERIES[selected]&&!CONTRACT_REVIEW_RULE_CATALOG.some(r=>r.engineType===selected)&&!/^执行和解协议$|^特别法合同$|^非合同$/u.test(selected))throw new Error('请选择系统已有的合同审核类别。');
  const series=engineSeries(selected);
  const rule=CONTRACT_REVIEW_RULE_CATALOG.find(r=>r.series===series&&!r.id.includes('.'));
  if(!selected||selected===SYSTEM_AUTO_CONTRACT_TYPE||!rule)throw new Error('请选择系统已有的合同审核类别。');
  // 同一系列仍保留已验证子类；跨系列时不携带原模型的具体类型/补充事项。
  if(prior&&isCurrentContractClassification(prior)&&prior.status==='accepted'&&prior.series===series)return {...prior,source:'manual'};
  const concrete=(CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(selected)||selected==='特别法合同'||selected==='非合同'?rule.engineType:selected;
  if(engineSeries(concrete)!==series)throw new Error('审核类别与规则不一致。');
  if(isNonContractClassificationRule(rule.id)){
    return {...pendingContractClassification(),source:'manual',status:'accepted',ruleId:rule.id,series,specificType:concrete,reviewContractType:rule.engineType,confidence:100,reason:'用户在项目设置中确认了非合同文件审核路径。',analogousRules:[],warnings:[]};
  }
  const priorCurrent=prior&&isCurrentContractClassification(prior)?prior:undefined;
  const analogousRules:ContractAnalogy[]=!needsContractAnalogy(rule.id,concrete)||!priorCurrent?[]
    :priorCurrent.ruleId==='other-mixed'?priorCurrent.analogousRules
      :priorCurrent.status==='accepted'&&getContractAnalogyRule(priorCurrent.ruleId)&&priorCurrent.evidence.length
        ?[{ruleId:priorCurrent.ruleId,reason:priorCurrent.reason,appliesTo:priorCurrent.specificType||priorCurrent.series,evidence:priorCurrent.evidence}]:[];
  return {...pendingContractClassification(),source:'manual',status:'accepted',ruleId:rule.id,series,specificType:concrete,reviewContractType:concrete,confidence:100,reason:'用户在项目设置中确认了审核类别。',analogousRules,warnings:[]};
}

export function classificationForReview(record:ContractClassification|undefined,selected:string,manual=false):ContractClassification{
  if(manual){const confirmed=manualContractClassification(selected,record);if(!isCurrentContractClassification(confirmed)||confirmed.status!=='accepted')throw new Error('请先确认混合合同的近似规则及适用范围后再审核。');return confirmed;}
  if(!record||!isCurrentContractClassification(record)||record.status!=='accepted')throw new Error('合同主审核类别尚未确认，请重新识别，或在项目设置中选择并保存合同类型后再审核。');
  if(selected&&selected!==SYSTEM_AUTO_CONTRACT_TYPE&&engineSeries(selected)!==record.series)throw new Error('审核设置与预审分类不一致，请在项目设置中确认并保存合同类别。');
  return record;
}

export function contractClassificationPromptCatalog():string{
  return CONTRACT_REVIEW_RULE_CATALOG.map(r=>`${r.id}｜${r.series}${r.id.includes('.')?'（和解/调解）':r.id==='non-contract'?'（非合同）':''}｜${r.boundary}`).join('\n');
}
